import { Box, Button, CircularProgress, Grid, TextField } from '@material-ui/core'
import { EntitySelect } from '../common/EntitySelect'
import React, { ChangeEvent, useEffect, useState } from 'react'
import * as XLSX from 'xlsx'
import { ErrorRecord } from '../../common/utils/form-generation/types'
import { Clinic, ClinicQuery } from '../../modules/clinics/models/Clinic'
import { Query, QueryParam } from '../../common/api/Query'
import { getClinicContainer } from '../../container/clinic-modules'
import { ClinicService } from '../../modules/clinics/services/ClinicService'
import { CLINIC_SERVICE_KEY } from '../../modules/clinics'
import { getAuthContainer } from '../../container/auth-modules'
import { AuthService } from '../../modules/auth/services/AuthService'
import { AUTH_SERVICE_KEY } from '../../modules/auth'
import { ResultPatientDataDTO } from '../../modules/script-executions/models/ResultPatientData'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import { lastValueFrom } from 'rxjs'
import { FILE_SERVICE_KEY } from '../../modules/files'
import { FileService } from '../../modules/files/services/FileService'
import { commonStyles } from '../../common/styles/Styles'
import { useStyles } from '../patients/Detail.styles'
import { COLOR_CLINICS } from 'routes/color-constants'
import { getFileContainer } from '../../container/file-module'
import { FileType } from './Form'
import { getPatientContainer } from '../../container/patient-module'
import { PatientService } from '../../modules/patients/services/PatientService'
import { PATIENT_SERVICE_KEY } from '../../modules/patients'
import { PatientQuery } from '../../modules/patients/models/PatientReceptiveness'
import { Permission } from '../../common/enums/Permissions'

type FileGroup = {
  barcodeID: string
  sampleName: string
  sampleRelation: File
  secuence: File[]
  alignment: File[]
}

type FileState = {
  sampleRelation: File[]
  secuence: File[]
  alignment: File[]
}

type FirstStep = {
  sampleRelation: string
  secuence: string
  alignment: string
  clinicID: string
}

type FirstStepProps = {
  increaseStep: () => void
  valuesToTable: (list: ResultPatientDataDTO[]) => void
  files: (ids: FileType[]) => void
  selectedClinic: (id: string) => void
}

const maxSampleNames = 20
const uploadConcurrency = 3
const clinicService = getClinicContainer().get<ClinicService>(CLINIC_SERVICE_KEY)
const authService = getAuthContainer().get<AuthService>(AUTH_SERVICE_KEY)
const fileService = getFileContainer().get<FileService>(FILE_SERVICE_KEY)
const patientService = getPatientContainer().get<PatientService>(PATIENT_SERVICE_KEY)

export const FirstStep = (props: FirstStepProps) => {
  const { t } = useTranslation()
  const [filesState, setFilesState] = useState<FileState>({
    sampleRelation: [],
    secuence: [],
    alignment: []
  })
  const [fileGroups, setFileGroups] = useState<FileGroup[]>([])
  const [clinics, setClinics] = useState<Clinic[]>([])
  const [selectedClinic, setSelectedClinic] = useState<string>(
    authService.get().clinics.length === 1 ? authService.get().clinics[0] : ''
  )
  const [errors, setErrors] = useState<ErrorRecord<FirstStep>>({} as ErrorRecord<FirstStep>)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const classes = useStyles({ color: COLOR_CLINICS })
  const styles2 = commonStyles()

  useEffect(() => {
    const query = []
    if (!authService.get().permissions.includes(Permission.viewAll)) {
      query.push(new QueryParam<ClinicQuery>('ids', authService.get().clinics))
    } else {
      clinicService.getAllClinics().subscribe((res) => setClinics(res.items))
    }
    clinicService
      .getFilteredList(
        new Query({
          query,
          sort: [{ field: 'name' }],
        })
      )
      .subscribe((res) => setClinics(res.items))
  }, [])

  useEffect(() => {
    selectedClinic && props.selectedClinic(selectedClinic)
  }, [selectedClinic])

  const handleSampleRelation = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    setFilesState(prev => ({ ...prev, sampleRelation: files }))
    setFileGroups([])
  }

  const handleMultipleFiles = (
    event: ChangeEvent<HTMLInputElement>,
    fileType: 'secuence' | 'alignment' 
  ) => {
    const files = Array.from(event.target.files || [])
    setFilesState(prev => ({ ...prev, [fileType]: files }))
    setFileGroups([])
  }

  const groupFilesByBarcode = async (): Promise<FileGroup[] | null> => {
    if (!filesState.sampleRelation.length) return null

    const groups: FileGroup[] = []
    
    for (const sampleFile of filesState.sampleRelation) {
      if (!sampleFile.name.endsWith('bc_summary.xls')) continue

      try {
        const workbook = XLSX.read(await sampleFile.arrayBuffer(), { type: 'binary' })
        const firstSheet = workbook.SheetNames[0]
        const excelRows = XLSX.utils.sheet_to_json(
          workbook.Sheets[firstSheet]
        ) as ResultPatientDataDTO[]

        for (const row of excelRows) {
          const barcodeID = Object.values(row)[0] as string
          const sampleName = Object.values(row)[1] as string

          groups.push({
            barcodeID,
            sampleName: sampleName || '',
            sampleRelation: sampleFile,
            secuence: filesState.secuence.filter(f => f.name.includes(barcodeID)),
            alignment: filesState.alignment.filter(f => f.name.includes(barcodeID))
          })
        }
      } catch (error) {
        console.error('Error reading Excel:', error)
      }
    }
    
    setFileGroups(groups)
    return groups
  }

  const validateSample = async (): Promise<string> => {
    if (!filesState.sampleRelation.length) return 'fileNotValidError'

    const validSamples = filesState.sampleRelation.filter(f => f.name.endsWith('bc_summary.xls'))
    if (!validSamples.length) return 'fileNotValidError'

    const workbook = XLSX.read(await validSamples[0].arrayBuffer(), { type: 'binary' })
    const firstSheet = workbook.SheetNames[0]
    const excelRows = XLSX.utils.sheet_to_json(
      workbook.Sheets[firstSheet]
    ) as ResultPatientDataDTO[]
    
    const values = excelRows.map((e) => ({
      barcodeID: Object.values(e)[0],
      sampleName: '' + Object.values(e)[1],
      biopsyMethod: '',
      biopsyDate: undefined,
      biopsyNumber: 1,
      cycleType: 100,
      p4Injection: undefined,
      hcgInjection: undefined,
      lhDate: undefined,
      pPlus: '',
      lhPlus: '',
      hcgPlus: '',
    }))

    if (!values.length || values.some((item, i) => values.findIndex((v) => v.sampleName === item.sampleName) !== i)) {
      return 'duplicatedSampleNames'
    }

    if (values.length > maxSampleNames) {
      return 'maxSampleNamesError'
    }

    const clinic = clinics.find((c) => c.id === selectedClinic)
    if (clinic && values.length > clinic.remainingCredits) {
      return 'insufficientCredits'
    }

    const sampleNames = values.map((v) => v.sampleName)
    const res = await lastValueFrom(
      patientService.getFilteredList(
        new Query({
          pager: { offset: 0, limit: sampleNames.length },
          query: [
            new QueryParam<PatientQuery>('sampleNames', sampleNames),
            new QueryParam<PatientQuery>('IDClinic', selectedClinic),
          ],
        })
      )
    )

    if (res.items.length !== sampleNames.length) {
      return (
        t('nonexistentSampleNames') +
        ': ' +
        sampleNames.filter((s) => !res.items.find((p) => p.sampleName === s)).join(', ')
      )
    }

    

    props.valuesToTable(values)
    return ''
  }

  const validate = async () => {
    const sampleError = await validateSample()
    if (sampleError) {
      setErrors({ sampleRelation: sampleError } as any)
      return
    }

    const groups = await groupFilesByBarcode()
    if (!groups || groups.length === 0) {
      setErrors({ sampleRelation: t('fileNotValidError') } as any)
      return
    }

    const errorCopy: any = {}
    
    groups.forEach((group, index) => {
      if (group.secuence.length === 0) {
        errorCopy[`secuence_${index}`] = t('missingFilesForBarcode', { barcode: group.barcodeID })
      }
      if (group.alignment.length === 0) {
        errorCopy[`alignment_${index}`] = t('missingFilesForBarcode', { barcode: group.barcodeID })
      }

    })

    if (!selectedClinic) {
      errorCopy['clinicID'] = t('noClinic')
    }

    setErrors(errorCopy)

    if (Object.keys(errorCopy).length) {
      return
    }

    await setData(groups)
  }

const setData = async (groups: FileGroup[]) => {
  setIsLoading(true)

  type UploadTask = { file: File; extension: string; mimeType: string; info: FileType }
  const ownerID = authService.get().id

  // Orden estable: summary, y luego por IonCode (fastq y después bam)
  const tasks: UploadTask[] = []
  const summaryFile = groups[0].sampleRelation
  tasks.push({
    file: summaryFile,
    extension: 'tsv',
    mimeType: 'text/tab-separated-values',
    info: { id: uuidv4(), name: summaryFile.name, ionCode: 'summary', type: 'summary' },
  })

  const sortedGroups = [...groups].sort((a, b) => a.barcodeID.localeCompare(b.barcodeID))
  for (const group of sortedGroups) {
    for (const file of group.secuence) {
      if (!file.name.endsWith('.fastq')) continue
      tasks.push({
        file,
        extension: 'fastq',
        mimeType: file.type,
        info: { id: uuidv4(), name: file.name, ionCode: group.barcodeID, type: 'sequence' as const },
      })
    }
    for (const file of group.alignment) {
      if (!file.name.includes('.bam')) continue
      tasks.push({
        file,
        extension: 'bam',
        mimeType: file.type,
        info: { id: uuidv4(), name: file.name, ionCode: group.barcodeID, type: 'alignment' as const },
      })
    }
  }

  // Pool de subidas concurrentes; ante el primer fallo no se lanzan más
  let next = 0
  let failed: { name: string; error: any } | null = null
  const worker = async () => {
    while (!failed) {
      const i = next++
      if (i >= tasks.length) return
      const task = tasks[i]
      try {
        await fileService.addStream(task.file, {
          id: task.info.id,
          name: task.file.name,
          mimeType: task.mimeType,
          extension: task.extension,
          ownerID,
          pgType: '',
        })
      } catch (error) {
        failed = failed || { name: task.file.name, error }
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(uploadConcurrency, tasks.length) }, worker))

  if (failed) {
    const { name, error } = failed as { name: string; error: any }
    console.error(`Error subiendo ${name}:`, error)
    setErrors({ sampleRelation: `${t('fileNotValidError')}: ${name}` } as any)
    setIsLoading(false)
    return
  }

  props.files(tasks.map((t) => t.info))
  setIsLoading(false)
  props.increaseStep()
}


  return (
    <>
      {!isLoading ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant={'outlined'}
              error={errors['sampleRelation'] !== undefined}
              id="sampleRelation"
              onChange={handleSampleRelation}
              type={'file'}
              label={`${t('sampleRelation')} * (bc_summary.xls)`}
              InputLabelProps={{ shrink: true }}
              helperText={errors['sampleRelation']}
              inputProps={{
                accept: '.xls',
                multiple: true,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              variant={'outlined'}
              id="secuence"
              onChange={(e) => handleMultipleFiles(e as ChangeEvent<HTMLInputElement>, 'secuence')}
              type={'file'}
              label={`${t('secuence')} * (.fastq)`}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                multiple: true,
                accept: '.fastq',
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              variant={'outlined'}
              id="alignment"
              onChange={(e) => handleMultipleFiles(e as ChangeEvent<HTMLInputElement>, 'alignment')}
              type={'file'}
              label={`${t('alignment')} * (.bam)`}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                multiple: true,
                accept: '.bam',
              }}
            />
          </Grid>

      

          {fileGroups.length > 0 && (
            <Grid item xs={12}>
              <Box p={2} border="1px solid #ddd" borderRadius={4}>
                <strong>{fileGroups.length} grupos encontrados:</strong>
                <ul style={{ marginTop: 8 }}>
                  {fileGroups.slice(0, 5).map((group, i) => (
                    <li key={i}>
                      <strong>{group.sampleName}</strong> ({group.barcodeID}): 
                      {group.secuence.length} seq, 
                      {group.alignment.length} align
                    </li>
                  ))}
                  {fileGroups.length > 5 && (
                    <li>...</li>
                  )}
                </ul>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <EntitySelect
              name={'name'}
              value={selectedClinic}
              error={errors['clinicID']}
              options={clinics}
              onChange={(value) => value?.id && setSelectedClinic(value.id)}
              label={t('clinic') + ' *'}
              pk={'id'}
              disabled={authService.get().clinics.length < 2}
            />
          </Grid>

          <Grid item xs={12}>
            <Box mt={3}>
              <Button
                onClick={validate}
                className={
                  filesState.sampleRelation.length && 
                  filesState.secuence.length && 
                  filesState.alignment.length && 
                  selectedClinic
                    ? classes.button
                    : styles2.button
                }
                variant={'contained'}
                disabled={
                  !(filesState.sampleRelation.length && 
                    filesState.secuence.length && 
                    filesState.alignment.length && 
                    selectedClinic)
                }>
                {t('continue')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box style={{ justifyContent: 'center', width: '100%', height: 200, display: 'flex', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      )}
    </>
  )
}
